import { BaseBlockProps, ColorId } from '../../defs/block';

export type FlowerFidgetBlockID = 'flowerFidget';

export type FlowerFidgetBlockType = 'flowerFidget';

export type FlowerFidgetBlockProps = {
  type: FlowerFidgetBlockType;
} & BaseBlockProps;

export const flowerFidgetBlockProps: Record<
  FlowerFidgetBlockID,
  FlowerFidgetBlockProps
> = {
  flowerFidget: {
    id: 'flf',
    type: 'flowerFidget',
    immobile: true,
  },
};

/**
 * The mapping of flower fidget colors to their numeric option values.
 * Each color corresponds to a specific bit in the option value.
 */
const flowerFidgetStateMap = {
  blue: 1,
  green: 2,
  red: 4,
  yellow: 8,
} as const satisfies Partial<Record<ColorId, number>>;

/**
 * All valid flower fidget colors.
 */
export const flowerFidgetColors = Object.keys(flowerFidgetStateMap) as Array<
  keyof typeof flowerFidgetStateMap
>;

/**
 * Type for a single flower fidget color.
 */
export type FlowerFidgetColor = keyof typeof flowerFidgetStateMap;

/**
 * State object for all flower fidget colors as flags true or false.
 */
export type FlowerFidgetState = Record<FlowerFidgetColor, boolean>;

/**
 * Converts a FlowerFidgetState object to its numeric option value.
 */
export function flowerFidgetStateToOption(state: FlowerFidgetState): number {
  return flowerFidgetColors.reduce(
    (option, color) =>
      option + (state[color] ? flowerFidgetStateMap[color] : 0),
    0,
  );
}

/**
 * Converts a numeric option value to a FlowerFidgetState object.
 */
export function flowerFidgetOptionToState(option: number): FlowerFidgetState {
  const state: Partial<FlowerFidgetState> = {};
  for (const color of flowerFidgetColors) {
    state[color] =
      (option & flowerFidgetStateMap[color]) === flowerFidgetStateMap[color];
  }
  return state as FlowerFidgetState;
}

/**
 * Checks if all flower fidget colors are set to true.
 */
export function flowerFidgetCheckStateComplete(
  state: FlowerFidgetState,
): boolean {
  return flowerFidgetColors.every((color) => !!state[color]);
}
